import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Shield, CheckCircle2, XCircle, Clock, Send } from "lucide-react";

export default function Verify() {
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ channel: "sms", to: "", length: 6, ttl_sec: 300 });
  const [last, setLast] = useState(null);
  const [check, setCheck] = useState({ verify_id: "", code: "" });
  const [checkResult, setCheckResult] = useState(null);

  const load = () => api.get("/verify").then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const send = async () => {
    try {
      const { data } = await api.post("/verify/send", { ...form, length: Number(form.length), ttl_sec: Number(form.ttl_sec) });
      setLast(data);
      setCheck({ verify_id: data.verify_id, code: "" });
      toast.success(`OTP sent via ${form.channel}. Verify ID: ${data.verify_id.slice(0, 8)}…`);
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  const runCheck = async () => {
    try {
      const { data } = await api.post("/verify/check", check);
      setCheckResult(data);
      if (data.verified) toast.success("Verified!");
      else toast.error(`Status: ${data.status} (${data.attempts_left} attempts left)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Check failed"); }
  };

  const StatusPill = ({ s }) => {
    const cls = s === "verified" ? "border-emerald-300 text-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 dark:text-emerald-400"
      : s === "expired" ? "border-zinc-300 text-zinc-700 bg-zinc-50 dark:bg-zinc-800 dark:text-zinc-400"
      : s === "max_attempts" ? "border-red-300 text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-400"
      : "border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400";
    return <Badge variant="outline" className={`rounded-sm text-[10px] ${cls}`} data-testid={`verify-status-${s}`}>{s}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="verify-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Auth</div>
          <h1 className="text-3xl font-black tracking-tighter">Verify (OTP)</h1>
          <p className="text-xs text-muted-foreground mt-1">Send a one-time code over SMS, WhatsApp, Voice or Email — then verify it via API.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm gap-2" data-testid="verify-new-button"><Send className="h-4 w-4" /> Send OTP</Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle>Send OTP</DialogTitle><DialogDescription>Any channel; multi-channel failover coming next.</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Channel</Label>
                <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}
                  className="h-9 w-full px-3 rounded-sm border border-border bg-background text-sm" data-testid="verify-channel">
                  <option value="sms">sms</option>
                  <option value="whatsapp">whatsapp</option>
                  <option value="voice">voice</option>
                  <option value="email">email</option>
                </select>
              </div>
              <div><Label>Recipient</Label><Input value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} placeholder="+91XXXXXXXXXX or email" className="rounded-sm" data-testid="verify-to" /></div>
              <div><Label>Code length</Label><Input type="number" min={4} max={10} value={form.length} onChange={(e) => setForm({ ...form, length: e.target.value })} className="rounded-sm" data-testid="verify-length" /></div>
              <div><Label>TTL seconds</Label><Input type="number" min={30} max={1800} value={form.ttl_sec} onChange={(e) => setForm({ ...form, ttl_sec: e.target.value })} className="rounded-sm" data-testid="verify-ttl" /></div>
            </div>
            <DialogFooter><Button onClick={send} className="rounded-sm" data-testid="verify-send-button">Send</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {last && (
        <Card className="rounded-sm shadow-none border-emerald-300 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-900/10">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800 dark:text-emerald-300">
              <CheckCircle2 className="h-4 w-4" /> OTP sent
            </div>
            <div className="text-xs font-mono text-muted-foreground">verify_id: {last.verify_id}</div>
            {last.demo_code && <div className="text-xs">Demo code (super admin only): <span className="font-mono font-bold text-emerald-700">{last.demo_code}</span></div>}
          </CardContent>
        </Card>
      )}

      <Card className="rounded-sm shadow-none" data-testid="verify-check-panel">
        <CardContent className="p-4 space-y-3">
          <div className="text-sm font-semibold flex items-center gap-2"><Shield className="h-4 w-4" /> Verify a code</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>verify_id</Label><Input value={check.verify_id} onChange={(e) => setCheck({ ...check, verify_id: e.target.value })} className="rounded-sm font-mono text-xs" data-testid="verify-check-id" /></div>
            <div><Label>Code</Label><Input value={check.code} onChange={(e) => setCheck({ ...check, code: e.target.value })} className="rounded-sm font-mono" data-testid="verify-check-code" /></div>
            <div className="flex items-end"><Button onClick={runCheck} className="rounded-sm w-full" data-testid="verify-check-button">Check</Button></div>
          </div>
          {checkResult && (
            <div className={`text-xs p-2 rounded-sm border ${checkResult.verified ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20" : "border-red-300 bg-red-50 dark:bg-red-900/20"}`}>
              {checkResult.verified ? <span className="text-emerald-700">✓ Verified</span> : <span className="text-red-700">✗ {checkResult.status}</span>} — {checkResult.attempts_left} attempts left
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-sm shadow-none">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2">Verify ID</th>
                <th className="px-3 py-2">Channel</th>
                <th className="px-3 py-2">To</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Attempts</th>
                <th className="px-3 py-2 text-right">Sent</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id} className="border-t border-border hover:bg-accent/40" data-testid={`verify-row-${s.id}`}>
                  <td className="px-3 py-2 font-mono text-[11px]">{s.id.slice(0, 12)}…</td>
                  <td className="px-3 py-2 uppercase text-xs">{s.channel}</td>
                  <td className="px-3 py-2 font-mono text-xs">{s.to}</td>
                  <td className="px-3 py-2"><StatusPill s={s.status} /></td>
                  <td className="px-3 py-2 text-xs">{s.attempts}/{s.max_attempts}</td>
                  <td className="px-3 py-2 text-right text-xs font-mono">{new Date(s.created_at).toLocaleString()}</td>
                </tr>
              ))}
              {sessions.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-10">No OTP sessions yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
